/**
 * The requirements traceability matrix.
 *
 * The columns come from the API rather than being listed here, because the RTM
 * is the report the program is actually held to and its shape belongs with
 * the model. The CSV is the same rows through the same endpoint, so what is on
 * screen and what gets mailed out cannot disagree.
 */

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DownloadIcon from '@mui/icons-material/Download'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { DataGrid } from '@mui/x-data-grid'
import type { GridColDef } from '@mui/x-data-grid'
import { ApiError, api } from '../api'
import {
  WRAPPING_GRID_PROPS,
  WRAPPING_GRID_SX,
  useFittedHeight,
} from '../components/common/grid-layout'
import { naturalComparator } from '../components/common/naturalCompare'
import { MONOSPACE } from '../theme'
import EntityFilterBar from '../components/entity/EntityFilterBar'
import { matchesQuery, readSearch, writeSearch } from '../components/entity/rowFilter'
import { useRememberedFilters } from '../components/entity/rememberedFilters'

interface Rtm {
  columns: string[]
  rows: Record<string, string>[]
}

/**
 * The RTM columns that hold an id, a name or a list of ids, which sort NATURALLY
 * (SUB_2 before SUB_10, issue #82). Every cell here is text, so this is a
 * decision about meaning: the statement is prose, and level, status, test level
 * and methods are choices, so those keep the grid's own comparator.
 */
function sortsNaturally(column: string): boolean {
  return (
    column === 'id' ||
    column.endsWith('_id') ||
    ['package', 'segment', 'subsystem', 'category', 'sources', 'verified_by', 'satisfied_by'].includes(
      column,
    )
  )
}

/** Turn a column name from the API into a heading. */
function heading(column: string): string {
  const words = column.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export default function Traceability() {
  // The RTM is the widest table in BEST - a column per verification case - so
  // it needs the sideways scrollbar within reach more than any other. See
  // components/common/grid-layout.
  const { ref, height } = useFittedHeight()
  const [rtm, setRtm] = useState<Rtm | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  /**
   * The search, in the URL under `search` as on every other list (issue #86).
   *
   * It was component state, so it was gone the moment anything else was opened
   * and the box was empty on the way back. In the URL it is a link, and
   * remembered so that the sidebar and the breadcrumbs put it back. Replace, not
   * push: typing must not fill up the back button.
   */
  const [params, setParams] = useSearchParams()
  const search = readSearch(params)
  const setSearch = (next: string) =>
    setParams((now) => writeSearch(now, next), { replace: true })
  useRememberedFilters()

  useEffect(() => {
    // Through the client, not a bare fetch: this screen used its own fetch and
    // so sent no X-BEST-Project header, leaving it to read whichever program
    // the server last resolved rather than the one the picker shows.
    api
      .rtm()
      .then(setRtm)
      .catch((failure: ApiError) => setError(failure))
  }, [])

  const columns = useMemo<GridColDef[]>(
    () =>
      (rtm?.columns ?? []).map((column) => ({
        field: column,
        headerName: heading(column),
        flex: column.includes('statement') ? 3 : 1,
        minWidth: column.includes('statement') ? 280 : 120,
        ...(sortsNaturally(column) ? { sortComparator: naturalComparator } : {}),
        renderCell:
          column === 'id' || column.endsWith('_id')
            ? (params) => (
                <Box sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>
                  {params.value as string}
                </Box>
              )
            : undefined,
      })),
    [rtm?.columns],
  )

  const rows = useMemo(() => {
    const all = (rtm?.rows ?? []).map((row, index) => ({ id: index, ...row }))
    if (!search) return all
    // Through the shared matcher: this already searched every column, but with
    // a single-substring test, so 'tbr structures' found nothing here while
    // finding rows everywhere else.  One term rule for every filter.
    return all.filter((row) => matchesQuery(row as Record<string, unknown>, search))
  }, [rtm?.rows, search])

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!rtm) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">Traceability</Typography>
          <Typography variant="body2" color="text.secondary">
            Every requirement, what it derives from, and what verifies it.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          href={api.rtmCsvUrl()}
          // Downloaded from the same endpoint, so the file and the screen
          // cannot drift apart.
          download="rtm.csv"
        >
          Export CSV
        </Button>
      </Stack>

      <EntityFilterBar plural="rows" value={search} onChange={setSearch} />

      <Paper sx={{ p: 1.5 }}>
        <Box ref={ref} sx={{ height }}>
          <DataGrid
            {...WRAPPING_GRID_PROPS}
            rows={rows}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
            pageSizeOptions={[25, 50, 100]}
            sx={WRAPPING_GRID_SX}
          />
        </Box>
      </Paper>
    </Stack>
  )
}
